import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  Autocomplete,
  Grid,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  PlayArrow as ActiveIcon,
  Pause as InactiveIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  fetchSymbolsForStrategy,
  activateStrategy,
  deactivateStrategy,
  updateStrategyConfig,
  deleteAssociation,
  enableAllForStrategy,
  disableAllForStrategy,
  clearSuccessMessage,
  clearErrors,
  addAvailableSymbol
} from './strategySymbolsSlice';
import { fetchStrategy } from '../visualStrategies/visualStrategiesSlice';
import { fetchMarketSymbols } from '../markets/marketsSlice';

const StrategyConfigurationPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { strategyId } = useParams();

  const {
    currentStrategySymbols,
    currentStrategyLoading,
    currentStrategyError,
    isActivating,
    isDeactivating,
    isUpdating,
    isDeleting,
    isBulkUpdating,
    successMessage,
    availableSymbols
  } = useSelector((state) => state.strategySymbols);

  const {
    currentStrategy
  } = useSelector((state) => state.visualStrategies);

  const {
    symbols: marketSymbols,
    symbolsLoading
  } = useSelector((state) => state.markets);

  // Local state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [selectedAssociation, setSelectedAssociation] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  
  // Configuration form state
  const defaultConfig = {
    enabled: true,
    maxSignalsPerDay: 5,
    timeframe: '1d'
  };

  const [config, setConfig] = useState(defaultConfig);

  useEffect(() => {
    if (strategyId) {
      dispatch(fetchStrategy(strategyId));
      dispatch(fetchSymbolsForStrategy(strategyId));
    }
  }, [dispatch, strategyId]);

  // Fetch available market symbols
  useEffect(() => {
    dispatch(fetchMarketSymbols());
  }, [dispatch]);

  useEffect(() => {
    if (successMessage) {
      setSnackbarOpen(true);
      setTimeout(() => {
        dispatch(clearSuccessMessage());
        setSnackbarOpen(false);
      }, 3000);
    }
  }, [successMessage, dispatch]);

  const handleBack = () => {
    navigate('/strategies');
  };

  const handleAddSymbol = () => {
    setSelectedSymbol('');
    setConfig(defaultConfig);
    setAddDialogOpen(true);
  };

  const handleEditAssociation = (association) => {
    setSelectedAssociation(association);
    setConfig(association.config || defaultConfig);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (association) => {
    setSelectedAssociation(association);
    setDeleteDialogOpen(true);
  };

  const handleToggleSymbol = async (symbol) => {
    if (!strategyId || !symbol) return;

    const association = currentStrategySymbols.find(
      (assoc) => assoc.symbol && assoc.symbol.toUpperCase() === symbol.toUpperCase()
    );

    // If association exists and is currently active, treat "disable" as delete
    if (association && association.isActive === 'true') {
      await dispatch(
        deleteAssociation({
          strategyId: association.strategyId,
          symbol: association.symbol
        })
      );
      return;
    }

    // If association exists but is inactive, reactivate with existing config
    if (association) {
      await dispatch(
        activateStrategy({
          strategyId: association.strategyId,
          symbol: association.symbol,
          config: association.config || defaultConfig
        })
      );
      return;
    }

    // No association yet – create one with the default config
    await dispatch(
      activateStrategy({
        strategyId,
        symbol,
        config: defaultConfig
      })
    );
  };

  const handleSubmitAdd = async () => {
    if (!selectedSymbol) return;

    await dispatch(activateStrategy({
      strategyId,
      symbol: selectedSymbol,
      config
    }));

    // Add to available symbols if it's a custom one
    dispatch(addAvailableSymbol(selectedSymbol));
    
    setAddDialogOpen(false);
    setSelectedSymbol('');
  };

  const handleSubmitEdit = async () => {
    if (!selectedAssociation) return;

    await dispatch(updateStrategyConfig({
      strategyId: selectedAssociation.strategyId,
      symbol: selectedAssociation.symbol,
      config
    }));

    setEditDialogOpen(false);
    setSelectedAssociation(null);
  };

  const handleSubmitDelete = async () => {
    if (!selectedAssociation) return;

    await dispatch(deleteAssociation({
      strategyId: selectedAssociation.strategyId,
      symbol: selectedAssociation.symbol
    }));

    setDeleteDialogOpen(false);
    setSelectedAssociation(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Build a unified list of symbols combining:
  // - Market symbols from the API (if available)
  // - Fallback / custom symbols from strategySymbols slice
  // - Any symbols already associated with this strategy
  const allSymbols = React.useMemo(() => {
    const baseSymbols =
      marketSymbols && marketSymbols.length > 0 ? marketSymbols : availableSymbols;

    const symbolSet = new Set(
      (baseSymbols || [])
        .map((sym) => (sym || '').toUpperCase())
        .filter(Boolean)
    );

    currentStrategySymbols.forEach((assoc) => {
      if (assoc?.symbol) {
        symbolSet.add(assoc.symbol.toUpperCase());
      }
    });

    return Array.from(symbolSet).sort();
  }, [marketSymbols, availableSymbols, currentStrategySymbols]);

  const handleEnableAll = async () => {
    if (!strategyId || allSymbols.length === 0) return;

    await dispatch(
      enableAllForStrategy({
        strategyId,
        symbols: allSymbols,
        defaultConfig,
      }),
    );
  };

  const handleDisableAll = async () => {
    if (!strategyId || currentStrategySymbols.length === 0) return;

    // Only disable symbols that actually have active associations
    const activeSymbols = currentStrategySymbols
      .filter((assoc) => assoc.isActive === 'true' && assoc.symbol)
      .map((assoc) => assoc.symbol);

    if (activeSymbols.length === 0) return;

    await dispatch(
      disableAllForStrategy({
        strategyId,
        symbols: activeSymbols,
      }),
    );
  };

  const hasActiveSymbols = currentStrategySymbols.some(
    (assoc) => assoc.isActive === 'true',
  );

  if (currentStrategyLoading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Typography>Loading strategy configuration...</Typography>
        </Box>
      </Container>
    );
  }

  if (currentStrategyError) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          {currentStrategyError}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" style={{ padding: '20px' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" gutterBottom>
            Strategy Configuration
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {currentStrategy?.name || 'Loading...'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleDisableAll}
            disabled={isBulkUpdating || !hasActiveSymbols}
          >
            Disable All
          </Button>
          <Button
            variant="outlined"
            onClick={handleEnableAll}
            disabled={isBulkUpdating || allSymbols.length === 0}
          >
            Enable All
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddSymbol}
            sx={{ 
              backgroundColor: 'var(--dwlf-accent)',
              color: 'var(--dwlf-bg)',
              '&:hover': {
                backgroundColor: 'var(--dwlf-text)',
              }
            }}
          >
            Add Symbol
          </Button>
        </Box>
      </Box>

      {/* Strategy Info */}
      {currentStrategy && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Strategy Details
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Description: {currentStrategy.description || 'No description'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Complexity: {currentStrategy.stats?.complexity || 'Unknown'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Last Modified: {formatDate(currentStrategy.updatedAt)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>
      )}

      {/* Symbols Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Symbol</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Configuration</TableCell>
                <TableCell>Signal Count</TableCell>
                <TableCell>Last Signal</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allSymbols.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No symbols available. Use "Add Symbol" to create your first symbol for this strategy.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                allSymbols.map((symbol) => {
                  const association = currentStrategySymbols.find(
                    (assoc) => assoc.symbol && assoc.symbol.toUpperCase() === symbol
                  );
                  const isActive = association?.isActive === 'true';

                  return (
                    <TableRow key={symbol} hover>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {symbol}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isActive}
                              onChange={() => handleToggleSymbol(symbol)}
                              disabled={isActivating || isDeactivating || isDeleting || isBulkUpdating}
                            />
                          }
                          label={isActive ? 'Active' : 'Inactive'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {association?.config
                            ? `Max: ${association.config.maxSignalsPerDay || 5}/day`
                            : 'Not configured'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Timeframe: {association?.config?.timeframe || defaultConfig.timeframe}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {association?.signalCount || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {association?.lastSignalAt
                            ? formatDate(association.lastSignalAt)
                            : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {association && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleEditAssociation(association)}
                                title="Edit Configuration"
                              >
                                <SettingsIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(association)}
                                disabled={isDeleting}
                                title="Delete"
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </motion.div>

      {/* Add Symbol Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Symbol to Strategy</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Autocomplete
              freeSolo
              options={marketSymbols && marketSymbols.length > 0 ? marketSymbols : availableSymbols}
              value={selectedSymbol}
              onChange={(event, newValue) => setSelectedSymbol(newValue || '')}
              onInputChange={(event, newInputValue) => setSelectedSymbol(newInputValue)}
              loading={symbolsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Symbol"
                  fullWidth
                  margin="normal"
                  helperText={symbolsLoading ? "Loading symbols..." : "Select from list or type a custom symbol (e.g., AAPL, BTC/USD)"}
                />
              )}
            />
            
            <TextField
              fullWidth
              label="Max Signals Per Day"
              type="number"
              value={config.maxSignalsPerDay}
              onChange={(e) => setConfig({ ...config, maxSignalsPerDay: parseInt(e.target.value) || 5 })}
              margin="normal"
              inputProps={{ min: 1, max: 50 }}
            />
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Timeframe</InputLabel>
              <Select
                value={config.timeframe}
                label="Timeframe"
                onChange={(e) => setConfig({ ...config, timeframe: e.target.value })}
              >
                <MenuItem value="1m">1 Minute</MenuItem>
                <MenuItem value="5m">5 Minutes</MenuItem>
                <MenuItem value="15m">15 Minutes</MenuItem>
                <MenuItem value="1h">1 Hour</MenuItem>
                <MenuItem value="4h">4 Hours</MenuItem>
                <MenuItem value="1d">1 Day</MenuItem>
                <MenuItem value="1w">1 Week</MenuItem>
              </Select>
            </FormControl>
            
            <FormControlLabel
              control={
                <Switch
                  checked={config.enabled}
                  onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                />
              }
              label="Enabled"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmitAdd} 
            variant="contained"
            disabled={!selectedSymbol || isActivating}
          >
            Add Symbol
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Configuration Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Symbol: {selectedAssociation?.symbol}
            </Typography>
            
            <TextField
              fullWidth
              label="Max Signals Per Day"
              type="number"
              value={config.maxSignalsPerDay}
              onChange={(e) => setConfig({ ...config, maxSignalsPerDay: parseInt(e.target.value) || 5 })}
              margin="normal"
              inputProps={{ min: 1, max: 50 }}
            />
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Timeframe</InputLabel>
              <Select
                value={config.timeframe}
                label="Timeframe"
                onChange={(e) => setConfig({ ...config, timeframe: e.target.value })}
              >
                <MenuItem value="1m">1 Minute</MenuItem>
                <MenuItem value="5m">5 Minutes</MenuItem>
                <MenuItem value="15m">15 Minutes</MenuItem>
                <MenuItem value="1h">1 Hour</MenuItem>
                <MenuItem value="4h">4 Hours</MenuItem>
                <MenuItem value="1d">1 Day</MenuItem>
                <MenuItem value="1w">1 Week</MenuItem>
              </Select>
            </FormControl>
            
            <FormControlLabel
              control={
                <Switch
                  checked={config.enabled}
                  onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                />
              }
              label="Enabled"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmitEdit} 
            variant="contained"
            disabled={isUpdating}
          >
            Update Configuration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Association</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the association between this strategy and {selectedAssociation?.symbol}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmitDelete} 
            color="error"
            disabled={isDeleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default StrategyConfigurationPage;